// Number of days before the renewal date the subscription is eligible
// to skip.
const SKIP_WINDOW_IN_DAYS = 7;

// Number of days before the renewal date the subscription can be customized
const CUSTOMIZE_WINDOW_IN_DAYS = 32;

/**
 * Returns the next renewal date given the subscription's renewal window and
 *   the given date.
 *
 * @param {Subscription} Subscription containing at least the renewal window
 * @param {Date=} Date representing the "current" date to check.
 *
 * @return {Date} Next renewal date
 */
const getNextRenewalDate = (subscription, fromDate = new Date()) => {
  const renewalDate = subscription.renewOn === '2nd' ? 2 : 15;
  const renewalYear = fromDate.getFullYear();
  const renewalMonth =
    fromDate.getDate() < renewalDate
      ? fromDate.getMonth()
      : fromDate.getMonth() + 1;

  return new Date(renewalYear, renewalMonth, renewalDate, 0, 0, 0, 0);
};

/**
 * Returns the renewal date for the current month as if the subscription renews on the 2nd. All subscriptions now have the same RAD window start time, https://app.asana.com/0/784868462049330/1200734291917267/f
 *
 * @param {!Subscription} subscription containing at least the renewal window
 * @param {Date=} fromDate representing the "current" date to check.
 *
 * @return {Date} Next renewal date on the 2nd
 */
const getNextRenewalDateForCustomizationStart = (
  subscription,
  fromDate = new Date(),
) => {
  const renewTimeDate = getNextRenewalDate(subscription, fromDate);

  renewTimeDate.setDate(2);
  return renewTimeDate;
};

/**
 * Returns the earliest time this subscription is eligible to be skipped
 *
 * @param {Subscription} Subscription containing at least the renewal window
 * @param {Date} Date representing the next time this subscription should renew
 *
 * @return {Date} Earliest date this subscription is eligible to be skipped
 */
const getSkipEligibilityDate = (subscription, nextRenewalDate) =>
  new Date(
    nextRenewalDate.getFullYear(),
    nextRenewalDate.getMonth(),
    nextRenewalDate.getDate() - SKIP_WINDOW_IN_DAYS,
    0,
    0,
    0,
    0,
  );

/**
 * Determines whether the given date limits allow for a subscription to skip
 *
 * @param {Date} Date the subscription is expected to renew
 * @param {Date} Date the subscription is eligible to skip
 * @param {Date=} Date representing the date to check from. Likely current time.
 *
 * @return {boolean} True if eligible to skip, false otherwise.
 */
// eslint-disable-next-line arrow-body-style
const isEligibleForSkip = (
  renewalDate,
  skipEligibilityDate,
  fromDate = new Date(),
) => {
  // Date should be after the start of the skip window but before the
  // renewal date
  return fromDate >= skipEligibilityDate && fromDate < renewalDate;
};

/**
 * Returns the earliest time this subscription is eligible to be customized
 *
 * @param {!Subscription} subscription containing at least the renewal window
 * @param {Date=} fromDate representing the "current" date to check.
 *
 * @return {Date} Earliest date this subscription is eligible to be customized
 */
const getCustomizeEligibilityDate = (subscription, fromDate) => {
  const nextRenewalDate = getNextRenewalDateForCustomizationStart(
    subscription,
    fromDate,
  );
  return new Date(
    nextRenewalDate.getFullYear(),
    nextRenewalDate.getMonth(),
    nextRenewalDate.getDate() - CUSTOMIZE_WINDOW_IN_DAYS,
    0,
    0,
    0,
    0,
  );
};

/**
 * Determines whether the given date limits allow for customizing a subscription
 *
 * @param {Date} Date the subscription is expected to renew
 * @param {Date} Date the subscription is eligible to customize
 * @param {Date=} Date representing the date to check from. Likely current time.
 *
 * @return {boolean} True if eligible to customize, false otherwise.
 */
// eslint-disable-next-line arrow-body-style
const isEligibleForCustomize = (
  renewalDate,
  customizeEligibilityDate,
  fromDate = new Date(),
) => {
  // Date should be after the start of the customize window but before the
  // renewal date
  return fromDate >= customizeEligibilityDate && fromDate < renewalDate;
};

module.exports = {
  getNextRenewalDate,
  getSkipEligibilityDate,
  isEligibleForSkip,
  getCustomizeEligibilityDate,
  isEligibleForCustomize,
  getNextRenewalDateForCustomizationStart,
};
